/**
 * EmployeeModal — Crear / Editar empleado con datos personales y contrato.
 */
import { todayISO } from '../../../lib/utils'
import { afpOptions } from '../constants/afp'
import { ISAPRE_NAMES, ISAPRE_OPTIONS } from '../constants/isapre'
import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { useForm, Controller } from 'react-hook-form'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import DatePicker from '@/components/DatePicker'
import { usePermission } from '@/hooks/usePermission'
import { inputCls, labelCls, errorCls } from '@/lib/formStyles'
import { extractErrorMessage, extractFieldErrors } from '../../../lib/errorUtils'
import type { Employee } from '../hooks/usePayroll'
import { usePrevisionalConfigs, useEmployeeBonuses, useCreateEmployeeBonus, useUpdateEmployeeBonus, useDeleteEmployeeBonus, useBonuses, useRecurringDeductions, useCreateRecurringDeduction, useUpdateRecurringDeduction, useDeleteRecurringDeduction, useEmployees, useEmployee } from '../hooks/usePayroll'
import { useCostCenters } from '@/modules/accounting/hooks/useAccounting'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

/** Payload de la configuración previsional que el modal entrega al padre para
 *  guardarla junto con el empleado (id presente = actualizar; ausente = crear). */
export interface PrevisionalPayload { id?: string; data: Record<string, unknown> }

/** Handle imperativo expuesto por PrevisionalSection vía ref. */
interface PrevisionalHandle {
  getPayload: () => { error?: string; payload?: PrevisionalPayload | null }
}

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<Employee>, previsional?: PrevisionalPayload | null) => void
  isPending: boolean
  initial?: Partial<Employee> | null
}

export default function EmployeeModal({ open, onClose, onSubmit, isPending, initial }: Props) {
  const { register, handleSubmit, reset, control, watch, formState: { errors } } = useForm<Partial<Employee>>()
  const previsionalRef = useRef<PrevisionalHandle | null>(null)
  const [prevError, setPrevError] = useState<string | null>(null)

  // El "Guardar cambios" persiste el empleado Y la configuración previsional
  // en un solo paso. La sección previsional entrega su payload vía ref; el
  // guardado real lo orquesta el padre (queda montado tras cerrar el modal).
  const submit = (data: Partial<Employee>) => {
    // Sin el detalle cargado, el formulario todavía no refleja al empleado y
    // guardar borraría los campos que el listado no trae.
    if (editando && !detalle) return
    const result = previsionalRef.current?.getPayload?.()
    if (result?.error) { setPrevError(result.error); return }
    setPrevError(null)
    onSubmit(data, result?.payload ?? null)
  }
  // `isForbidden`: el endpoint de centros de costo exige el módulo de
  // contabilidad, que un usuario de nómina puede no tener. Sin distinguirlo, el
  // autocompletado quedaba vacío y parecía que no hay centros configurados.
  const { data: ccData, isForbidden: ccForbidden } = useCostCenters({ is_active: 'true' })
  const costCenters = (ccData as any)?.cost_centers ?? (ccData as any)?.results ?? []
  const { data: empsData } = useEmployees({ active: 'true', page_size: 9999 })
  const otherEmployees = ((empsData as any)?.employees ?? []).filter((e: any) => e.id !== initial?.id)

  // 🔴 Al EDITAR hay que poblar el formulario con el DETALLE del empleado, no con
  // el objeto del listado: `EmployeeListSerializer` devuelve 14 campos y el resto
  // (email, teléfono, dirección, fecha de nacimiento, género, nacionalidad,
  // estado civil y los tres datos bancarios) queda fuera. Con `reset(initial)`
  // esos campos quedaban vacíos en el form y al guardar SE BORRABAN, sin que el
  // usuario los hubiera tocado.
  //
  // Pasó de verdad: 5 empleados de zenlatam perdieron datos entre mayo y agosto
  // de 2026, cuatro de ellos su banco, tipo y número de cuenta — con los que se
  // les paga el sueldo. Ver ops-rollback/2026-08-25_zenlatam_restaurar_datos_empleados.md
  const editando = Boolean(initial?.id)
  const { data: detalle, isLoading: cargandoDetalle } = useEmployee(initial?.id || '')

  useEffect(() => {
    if (!open) return
    if (editando) {
      // Solo cuando llegó el detalle completo. Resetear con `initial` mientras
      // carga volvería a dejar medio formulario vacío.
      if (detalle) reset(detalle)
    } else {
      reset({ status: 'active', is_active: true, nationality: 'Chilena', worker_type: 'dependiente' })
    }
  }, [open, editando, detalle, reset])

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  const isEdit = !!initial?.id
  const workerType = watch('worker_type') || 'dependiente'
  // Los campos de acreditación solo aplican si hay años previos que acreditar.
  const priorYears = Number(watch('prior_service_years') ?? 0) || 0

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar empleado' : 'Nuevo empleado'} size="2xl">
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        {/* Datos personales */}
        <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider">Datos Personales</h3>
        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Nombres"
            required
            placeholder="María"
            inputProps={register('first_name', { required: 'Obligatorio' })}
            error={errors.first_name?.message}
          />
          <TextField
            dense
            label="Apellidos"
            required
            placeholder="González Soto"
            inputProps={register('last_name', { required: 'Obligatorio' })}
            error={errors.last_name?.message}
          />
          <TextField
            dense
            label="RUT"
            required
            placeholder="12.345.678-5"
            inputProps={register('rut', { required: 'Obligatorio' })}
            error={errors.rut?.message}
          />
          <div>
            <label htmlFor="employeemodal-birth-date" className={labelCls}>Fecha de Nacimiento</label>
            <Controller name="birth_date" control={control}
              render={({ field }) => <DatePicker id="employeemodal-birth-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />} />
          </div>
          <SelectField
            dense
            label="Género"
            selectProps={register('gender')}
          >
            <option value="">—</option>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
            <option value="O">Otro</option>
          </SelectField>
          <SelectField
            dense
            label="Estado Civil"
            selectProps={register('marital_status')}
          >
            <option value="">—</option>
            <option value="soltero">Soltero/a</option>
            <option value="casado">Casado/a</option>
            <option value="divorciado">Divorciado/a</option>
            <option value="viudo">Viudo/a</option>
          </SelectField>
          <TextField
            dense
            label="Nacionalidad"
            placeholder="Chilena"
            inputProps={register('nationality')}
          />
          <div>
            <label htmlFor="employeemodal-family-dependents" className={labelCls}>Cargas familiares</label>
            <input id="employeemodal-family-dependents" type="number" min="0" step="1"
              {...register('family_dependents', {
                setValueAs: (v) => {
                  const n = Number(v)
                  return v === '' || v == null || Number.isNaN(n) ? 0 : Math.max(0, Math.trunc(n))
                },
              })}
              placeholder="0" className={inputCls} />
            <p className="text-xs text-content-muted mt-1">Determina la asignación familiar según el tramo de renta.</p>
          </div>
          <TextField
            dense
            label="Teléfono"
            placeholder="+56 9 1234 5678"
            inputProps={register('phone')}
          />
          <TextField
            dense
            className="col-span-2"
            label="Correo corporativo"
            placeholder="maria@gmail.com"
            inputProps={{ ...register('email'), type: 'email' }}
          />
          <TextField
            dense
            className="col-span-2"
            label="Dirección"
            placeholder="Av. Providencia 1234, Santiago"
            inputProps={register('address')}
          />
        </div>

        {/* Datos laborales */}
        <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider pt-2">Datos Laborales</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="employeemodal-worker-type" className={labelCls}>Tipo de trabajador</label>
            <select id="employeemodal-worker-type" {...register('worker_type')} className={inputCls}>
              <option value="dependiente">Trabajador dependiente</option>
              <option value="empresario">Sueldo empresarial (socio/dueño)</option>
            </select>
            {workerType === 'empresario' && (
              <p className="text-xs text-content-muted mt-1">
                Sueldo empresarial (Art. 31 N°6 LIR): no hay relación laboral. No aplica gratificación,
                horas extra, asignación familiar, cesantía ni mutual. El monto asignado tributa Impuesto
                Único de 2ª Categoría y las cotizaciones AFP/salud son voluntarias. El monto mensual se
                define en el contrato (Sueldo Base). Debe ser razonable respecto de la labor efectiva.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="employeemodal-hire-date" className={labelCls}>Fecha de Contratación <span className="text-danger-solid">*</span></label>
            <Controller name="hire_date" control={control} rules={{ required: 'Obligatorio' }}
              render={({ field }) => <DatePicker id="employeemodal-hire-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} required />} />
            {errors.hire_date && <p className={errorCls}>{errors.hire_date.message}</p>}
          </div>
          <div>
            <label htmlFor="employeemodal-prior-service-years" className={labelCls}>Años de Servicio Anteriores</label>
            <input id="employeemodal-prior-service-years" type="number" step="0.01" min="0" max="10"
              {...register('prior_service_years', { setValueAs: v => (v === '' || v == null ? 0 : v) })} className={inputCls} placeholder="0" />
            <p className="text-xs text-content-muted mt-1">
              Años trabajados para otros empleadores que el trabajador acreditó con su
              certificado de AFP. Suman para el feriado progresivo (Art. 68), con tope de
              10 años. No cuentan para la indemnización por años de servicio.
            </p>
          </div>

          {/* Acreditación del servicio anterior. Solo aparece cuando hay años
              cargados: si son 0 no hay nada que acreditar y son dos campos de
              ruido. La Dirección del Trabajo exige el medio de prueba y que se
              presente antes de usar el feriado básico del año, así que la fecha
              define desde cuándo corre el beneficio. */}
          {priorYears > 0 && (
            <>
              <div>
                <label htmlFor="employeemodal-prior-service-accredited-on" className={labelCls}>
                  Fecha de acreditación <span className="text-danger-solid">*</span>
                </label>
                <Controller name="prior_service_accredited_on" control={control}
                  render={({ field }) => (
                    <DatePicker id="employeemodal-prior-service-accredited-on" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} />
                  )} />
                {errors.prior_service_accredited_on && (
                  <p className={errorCls}>{errors.prior_service_accredited_on.message as string}</p>
                )}
                <p className="text-xs text-content-muted mt-1">
                  Fecha en que el trabajador presentó el respaldo. El feriado progresivo que
                  depende de estos años corre <strong>desde aquí</strong>: la Dirección del
                  Trabajo exige acreditarlos antes de usar el feriado básico del año, o se
                  pierde ese período.
                </p>
              </div>
              <div>
                <label htmlFor="employeemodal-prior-service-document" className={labelCls}>
                  Documento que lo acredita <span className="text-danger-solid">*</span>
                </label>
                <select id="employeemodal-prior-service-document" {...register('prior_service_document')} className={inputCls}>
                  <option value="">Seleccionar...</option>
                  <option value="afp_certificate">Certificado de cotizaciones de AFP</option>
                  <option value="labor_inspection">Certificación de la Inspección del Trabajo</option>
                  <option value="public_instrument">Otro instrumento público (sentencia, convenio, escritura)</option>
                  <option value="judicial">Información para perpetua memoria (judicial)</option>
                </select>
                {errors.prior_service_document && (
                  <p className={errorCls}>{errors.prior_service_document.message as string}</p>
                )}
                <p className="text-xs text-content-muted mt-1">
                  Guarda el respaldo en la carpeta <strong>Vacaciones</strong> de la pestaña
                  Documentos, para que quede en el expediente del trabajador.
                </p>
              </div>
            </>
          )}
          <SelectField
            dense
            label="Estado"
            selectProps={register('status')}
          >
            <option value="active">Activo</option>
            <option value="on_leave">Con Licencia</option>
            <option value="terminated">Desvinculado</option>
          </SelectField>
          <div>
            <label htmlFor="empleado-centro-costo" className={labelCls}>Centro de Costo</label>
            {ccForbidden ? (
              <p className="text-[11px] text-content-muted py-2">
                No tienes permiso para ver los centros de costo. Pide el permiso de{' '}
                <strong>Centros de Costo</strong> a un administrador para poder imputar las
                remuneraciones.
              </p>
            ) : (
              <>
                <Controller name="cost_center" control={control}
                  render={({ field }) => (
                    <EntityAutocomplete id="empleado-centro-costo"
                      options={costCenters.map((c: any) => ({ id: c.id, label: `${c.code} — ${c.name}` }))}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="— Sin asignar —"
                      allowClear clearLabel="Sin asignar"
                    />
                  )} />
                <p className="text-[10px] text-content-subtle mt-1">Las remuneraciones se imputan a este centro.</p>
              </>
            )}
          </div>
          <div>
            <label htmlFor="employeemodal-supervisor" className={labelCls}>Jefe Directo</label>
            <Controller name="supervisor" control={control}
              render={({ field }) => (
                <EntityAutocomplete id="employeemodal-supervisor"
                  options={(otherEmployees || []).map((e: any) => ({ id: e.id, label: e.full_name }))}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="— Sin asignar —"
                  allowClear clearLabel="Sin asignar"
                />
              )} />
          </div>
          <div>
            <label htmlFor="employeemodal-backup-employee" className={labelCls}>Suplente</label>
            <Controller name="backup_employee" control={control}
              render={({ field }) => (
                <EntityAutocomplete id="employeemodal-backup-employee"
                  options={(otherEmployees || []).map((e: any) => ({ id: e.id, label: e.full_name }))}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="— Sin asignar —"
                  allowClear clearLabel="Sin asignar"
                />
              )} />
          </div>
        </div>

        {/* Forma de pago / cuenta bancaria */}
        <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider pt-2">Forma de Pago</h3>
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            dense
            label="Método"
            selectProps={register('payment_method')}
          >
            <option value="transfer">Transferencia Electrónica</option>
            <option value="check">Cheque</option>
            <option value="cash">Efectivo</option>
            <option value="vale_vista">Vale Vista</option>
          </SelectField>
          <SelectField
            dense
            label="Banco"
            selectProps={register('bank_name')}
          >
            <option value="">— Sin banco —</option>
            <option value="estado">BancoEstado</option>
            <option value="chile">Banco de Chile</option>
            <option value="santander">Banco Santander</option>
            <option value="bci">Banco BCI</option>
            <option value="itau">Banco Itaú</option>
            <option value="scotiabank">Scotiabank</option>
            <option value="bice">Banco BICE</option>
            <option value="security">Banco Security</option>
            <option value="falabella">Banco Falabella</option>
            <option value="ripley">Banco Ripley</option>
            <option value="consorcio">Banco Consorcio</option>
            <option value="internacional">Banco Internacional</option>
            <option value="hsbc">HSBC</option>
            <option value="jpmorgan">JPMorgan Chase</option>
            <option value="caja_andes">Caja Los Andes</option>
            <option value="coopeuch">Coopeuch</option>
            <option value="other">Otro</option>
          </SelectField>
          <SelectField
            dense
            label="Tipo de Cuenta"
            selectProps={register('bank_account_type')}
          >
            <option value="">— Sin tipo —</option>
            <option value="checking">Cuenta Corriente</option>
            <option value="vista">Cuenta Vista</option>
            <option value="savings">Cuenta de Ahorro</option>
            <option value="rut">Cuenta RUT</option>
            <option value="other">Otra</option>
          </SelectField>
          <TextField
            dense
            label="N° de Cuenta"
            placeholder="000123456789"
            inputProps={register('bank_account_number')}
          />
        </div>

        {/* Previsional + Bonos + Descuentos recurrentes — solo en edición */}
        {isEdit && initial?.id && (
          <>
            <PrevisionalSection ref={previsionalRef} employeeId={initial.id} workerType={workerType} onClearError={() => setPrevError(null)} />
            <BonusSection employeeId={initial.id} />
            <RecurringDeductionSection employeeId={initial.id} />
          </>
        )}

        {prevError && (
          <p className="text-sm text-danger-content-soft text-right">{prevError}</p>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-divider">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">Cancelar</button>
          <button type="submit" disabled={isPending || (editando && cargandoDetalle)}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear empleado'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Previsional Section ────────────────────────────────────────────────────

const AFP_OPTIONS = afpOptions(false)

const PrevisionalSection = forwardRef<PrevisionalHandle, { employeeId: string; workerType?: string; onClearError?: () => void }>(
  function PrevisionalSection({ employeeId, workerType, onClearError }, ref) {
  const { data } = usePrevisionalConfigs({ employee_id: employeeId, current: 'true' })
  const config = data?.configs?.[0]

  const isEmpresario = workerType === 'empresario'

  const [afp, setAfp] = useState('')
  const [healthSystem, setHealthSystem] = useState('fonasa')
  const [isapreName, setIsapreName] = useState('')
  const [isaprePlanUf, setIsaprePlanUf] = useState('0')
  const [isapreFunNumber, setIsapreFunNumber] = useState('')
  const [apvAmount, setApvAmount] = useState('0')
  const [apvRegime, setApvRegime] = useState('')
  const [cuenta2Amount, setCuenta2Amount] = useState('0')
  const [depositoConvenido, setDepositoConvenido] = useState('0')
  const [cotiza, setCotiza] = useState(false)
  const [exentoReforma, setExentoReforma] = useState(false)
  const [esPensionado, setEsPensionado] = useState(false)
  const [suspendeAfp, setSuspendeAfp] = useState(false)

  useEffect(() => {
    if (config) {
      setAfp(config.afp); setHealthSystem(config.health_system)
      setIsapreName(config.isapre_name || ''); setIsaprePlanUf(config.isapre_plan_uf || '0')
      setIsapreFunNumber(config.isapre_fun_number || '')
      setApvAmount(config.apv_amount || '0'); setApvRegime(config.apv_regime || '')
      setCuenta2Amount(config.cuenta2_amount || '0'); setDepositoConvenido(config.deposito_convenido_amount || '0')
      setCotiza(Boolean(config.cotiza_voluntariamente))
      setExentoReforma(Boolean(config.exento_reforma))
      setEsPensionado(Boolean(config.es_pensionado))
      setSuspendeAfp(Boolean(config.suspende_afp_por_edad))
    }
  }, [config])

  // Sueldo empresarial: las cotizaciones son voluntarias, así que la AFP solo es
  // obligatoria si el socio decide cotizar. Para dependientes, siempre.
  const requireAfp = !isEmpresario || cotiza
  const showContributionFields = !isEmpresario || cotiza

  // El padre llama a esto al guardar el empleado. Devuelve:
  //  - {} si no hay nada que guardar (sección intacta y sin previsión previa).
  //  - { error } si falta la AFP cuando es obligatoria.
  //  - { payload } listo para crear (sin id) o actualizar (con id).
  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const touched = !!afp || healthSystem !== 'fonasa' || parseFloat(apvAmount || '0') > 0
        || parseFloat(cuenta2Amount || '0') > 0 || parseFloat(depositoConvenido || '0') > 0
        || (isEmpresario && cotiza) || exentoReforma || esPensionado || suspendeAfp
      if (!config?.id && !touched) return {}  // nada que guardar
      if (requireAfp && !afp) return { error: 'Seleccionar la AFP para guardar la configuración previsional.' }
      if (showContributionFields && healthSystem === 'isapre' && !ISAPRE_NAMES[isapreName]) {
        return { error: 'Seleccionar la isapre para guardar la configuración previsional.' }
      }
      return {
        payload: {
          id: config?.id,
          data: {
            employee: employeeId, afp, health_system: healthSystem,
            isapre_name: isapreName, isapre_plan_uf: isaprePlanUf,
            isapre_fun_number: isapreFunNumber,
            apv_amount: apvAmount, apv_regime: apvRegime,
            cuenta2_amount: cuenta2Amount, deposito_convenido_amount: depositoConvenido,
            cotiza_voluntariamente: cotiza, exento_reforma: exentoReforma,
            es_pensionado: esPensionado, suspende_afp_por_edad: suspendeAfp,
            is_current: true,
          },
        },
      }
    },
  }), [afp, healthSystem, isapreName, isaprePlanUf, isapreFunNumber, apvAmount, apvRegime, cuenta2Amount, depositoConvenido, cotiza, exentoReforma, esPensionado, suspendeAfp, requireAfp, isEmpresario, config?.id, employeeId])

  return (
    <div>
      <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider pt-2 mb-1">Configuración Previsional</h3>
      <p className="text-xs text-content-muted mb-3">Se guarda junto con el empleado al presionar «Guardar cambios».</p>

      {isEmpresario && (
        <label className="flex items-center gap-2 mb-3 text-sm text-content">
          <input type="checkbox" checked={cotiza}
            onChange={e => { setCotiza(e.target.checked); onClearError?.() }}
            className="h-4 w-4 rounded border-divider text-primary focus:ring-primary" />
          Cotiza previsión voluntaria (AFP / Salud) — sueldo empresarial
        </label>
      )}

      {showContributionFields ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="empleado-afp" className={labelCls}>AFP {requireAfp && <span className="text-danger-solid">*</span>}</label>
            <select id="empleado-afp" value={afp} onChange={e => { setAfp(e.target.value); onClearError?.() }} className={inputCls}>
              <option value="">Seleccionar...</option>
              {AFP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <SelectField
            dense
            label="Sistema de Salud"
            value={healthSystem}
            onChange={e => setHealthSystem(e.target.value)}
          >
            <option value="fonasa">Fonasa (7%)</option>
            <option value="isapre">Isapre</option>
          </SelectField>
          {healthSystem === 'isapre' && (
            <>
              <SelectField
                dense
                label="Isapre"
                required
                value={ISAPRE_NAMES[isapreName] ? isapreName : ''}
                onChange={e => { setIsapreName(e.target.value); onClearError?.() }}
              >
                <option value="">Seleccionar...</option>
                {ISAPRE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </SelectField>
              <TextField
                dense
                label="Plan Isapre (UF)"
                value={isaprePlanUf}
                onChange={e => setIsaprePlanUf(e.target.value)}
                inputProps={{ type: 'number', step: '0.0001' }}
              />
              <TextField
                dense
                label="N° de FUN"
                value={isapreFunNumber}
                onChange={e => setIsapreFunNumber(e.target.value)}
                placeholder="21354789"
                inputProps={{ maxLength: 16, title: 'Número del Formulario Único de Notificación del contrato de salud. Va en el campo 76 del archivo Previred y está en el propio FUN, que se puede adjuntar más abajo.' }}
              />
            </>
          )}
          <TextField
            dense
            label="APV Mensual ($)"
            value={apvAmount}
            onChange={e => setApvAmount(e.target.value)}
            inputProps={{ type: 'number', step: '1', min: '0' }}
          />
          {parseFloat(apvAmount) > 0 && (
            <SelectField
              dense
              label="Régimen APV"
              value={apvRegime}
              onChange={e => setApvRegime(e.target.value)}
            >
              <option value="">—</option>
              <option value="A">Régimen A</option>
              <option value="B">Régimen B</option>
            </SelectField>
          )}
          <div>
            <label htmlFor="employeemodal-cuenta-2-ahorro-voluntario" className={labelCls}>Cuenta 2 / Ahorro Voluntario ($)</label>
            <input id="employeemodal-cuenta-2-ahorro-voluntario" type="number" step="1" min="0" value={cuenta2Amount} onChange={e => setCuenta2Amount(e.target.value)} className={inputCls} />
            <p className="text-[11px] text-content-muted mt-0.5">Baja el líquido; no rebaja el impuesto.</p>
          </div>
          <div>
            <label htmlFor="employeemodal-deposito-convenido" className={labelCls}>Depósito Convenido ($)</label>
            <input id="employeemodal-deposito-convenido" type="number" step="1" min="0" value={depositoConvenido} onChange={e => setDepositoConvenido(e.target.value)} className={inputCls} />
            <p className="text-[11px] text-content-muted mt-0.5">Baja el líquido y rebaja la base del impuesto (tope 900 UF/año).</p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-content-muted">
          Sin cotización previsional: el sueldo empresarial tributa Impuesto Único de 2ª Categoría
          sobre el monto íntegro asignado.
        </p>
      )}

      {!isEmpresario && (
        <div className="mt-3 space-y-3">
          <label className="flex items-start gap-2 text-sm text-content">
            <input type="checkbox" checked={esPensionado}
              onChange={e => { setEsPensionado(e.target.checked); onClearError?.() }}
              className="mt-0.5 h-4 w-4 rounded border-divider text-primary focus:ring-primary" />
            <span>
              Pensionado / jubilado
              <span className="block text-xs text-content-muted">
                Ya está pensionado por vejez o invalidez total: por ley no cotiza AFP ni SIS y queda
                fuera del Seguro de Cesantía. Sí cotiza el 7% de salud y sí tiene derecho a
                gratificación.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-content">
            <input type="checkbox" checked={suspendeAfp} disabled={esPensionado}
              onChange={e => { setSuspendeAfp(e.target.checked); onClearError?.() }}
              className="mt-0.5 h-4 w-4 rounded border-divider text-primary focus:ring-primary disabled:opacity-50" />
            <span className={esPensionado ? 'opacity-50' : undefined}>
              Suspende su cotización de AFP por edad
              <span className="block text-xs text-content-muted">
                Solo para quien está en edad de pensión (hombres ≥65, mujeres ≥60) y <strong>no</strong> está
                pensionado. El Art. 69 del DL 3.500 lo exime de cotizar, pero acogerse es su decisión:
                mientras esto esté desmarcado sigue cotizando con normalidad. No afecta el Seguro de
                Cesantía ni el 7% de salud.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-content">
            <input type="checkbox" checked={exentoReforma}
              onChange={e => { setExentoReforma(e.target.checked); onClearError?.() }}
              className="mt-0.5 h-4 w-4 rounded border-divider text-primary focus:ring-primary" />
            <span>
              Exento del aporte de Reforma Previsional (Ley 21.735)
              <span className="block text-xs text-content-muted">
                Actívalo para pensionados por vejez/invalidez, hombres ≥65 o mujeres ≥60 no pensionados, y
                afiliados voluntarios: el empleador no paga la cotización adicional por este trabajador.
              </span>
            </span>
          </label>
        </div>
      )}
    </div>
  )
})

// ─── Bonus Section ──────────────────────────────────────────────────────────

/**
 * Qué bonos del catálogo se pueden asignar todavía a este trabajador.
 *
 * 🔴 Un bono cuya única asignación **ya terminó** vuelve a estar disponible, y eso
 * es el fix: antes el selector escondía todo bono que apareciera en la lista de
 * asignados, sin mirar la vigencia. Con la comisión de julio cerrada el 31-07, el
 * bono «Comisión» ya no se ofrecía, así que la única forma de cargar la de agosto
 * era reescribirle el monto y las fechas al registro de julio — de ahí el reporte
 * de RRHH: «solo me deja cambiar la fecha».
 *
 * Una asignación **programada** (que empieza el mes que viene) sí bloquea: agregar
 * otra encima se cruzaría con ella y el backend la rechazaría.
 */
export function bonosDisponiblesParaAsignar(
  catalogo: { id: string; is_active: boolean }[],
  asignados: { bonus: string; effective_from?: string | null; effective_to?: string | null }[],
) {
  const conAsignacionViva = new Set(
    asignados
      .filter(a => !estadoDeVigencia(a.effective_from, a.effective_to).vencido)
      .map(a => a.bonus),
  )
  return catalogo.filter(b => b.is_active && !conAsignacionViva.has(b.id))
}

/**
 * El mensaje que se le muestra a quien está editando.
 *
 * Estas mutaciones se guardaban sin `onError`, así que un rechazo del backend no
 * dejaba rastro en pantalla: el usuario escribía un monto, no pasaba nada, y
 * concluía que el campo «no lo deja modificar» ([[mutacion-sin-onerror-falla-en-silencio]]).
 * Con la validación de vigencias solapadas eso pasó a ser un caso frecuente.
 */
function mensajeDeError(e: unknown): string {
  const campos = extractFieldErrors(e)
  if (campos) return Object.values(campos)[0]
  return extractErrorMessage(e, 'No se pudo guardar el cambio. Revisa los datos e intenta de nuevo.')
}

/** El primer día del mes en curso, en formato ISO. */
function primerDiaDelMes(): string {
  const hoy = new Date()
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`
}

function BonusSection({ employeeId }: { employeeId: string }) {
  const { data: assignedData } = useEmployeeBonuses({ employee_id: employeeId })
  const { data: allBonusesData } = useBonuses()
  const { mutate: assign, isPending: assigning } = useCreateEmployeeBonus()
  const [selectedBonus, setSelectedBonus] = useState('')
  const [error, setError] = useState<string | null>(null)

  const assigned = assignedData?.assignments || []
  const allBonuses = allBonusesData?.bonuses || []

  // Vigentes y programados arriba; los terminados abajo, como historial. Se
  // conservan a propósito: son el respaldo de lo que se pagó en cada mes.
  const historicos = assigned.filter((a: any) => estadoDeVigencia(a.effective_from, a.effective_to).vencido)
  const activos = assigned.filter((a: any) => !estadoDeVigencia(a.effective_from, a.effective_to).vencido)

  const available = bonosDisponiblesParaAsignar(allBonuses, assigned)

  const handleAssign = () => {
    if (!selectedBonus) return
    setError(null)
    // Si el bono ya tuvo una asignación que terminó, la nueva arranca este mes: sin
    // fecha de inicio aplicaría «desde siempre» y se cruzaría con la anterior, que
    // es justo lo que el backend rechaza.
    const yaTuvo = assigned.some((a: any) => a.bonus === selectedBonus)
    assign(
      { employee: employeeId, bonus: selectedBonus, ...(yaTuvo ? { effective_from: primerDiaDelMes() } : {}) },
      {
        onSuccess: () => setSelectedBonus(''),
        onError: (e: any) => setError(mensajeDeError(e)),
      },
    )
  }

  return (
    <div>
      <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider pt-2 mb-3">
        Bonos Asignados ({activos.length})
      </h3>
      {activos.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {activos.map((a: any) => (
            <BonusRow key={a.id} assignment={a} />
          ))}
        </div>
      )}
      {historicos.length > 0 && (
        <details className="mb-3">
          <summary className="text-xs text-content-subtle cursor-pointer hover:text-content-muted">
            Historial ({historicos.length}) — asignaciones terminadas
          </summary>
          <div className="space-y-1.5 mt-2">
            {historicos.map((a: any) => (
              <BonusRow key={a.id} assignment={a} />
            ))}
          </div>
        </details>
      )}
      {error && <p className={`${errorCls} mb-2`}>{error}</p>}
      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <select aria-label="Asignar bono" value={selectedBonus} onChange={e => { setSelectedBonus(e.target.value); setError(null) }} className={`${inputCls} flex-1 text-xs`}>
            <option value="">Asignar bono...</option>
            {available.map((b: any) => (
              <option key={b.id} value={b.id}>{b.code} — {b.name} (${parseFloat(b.default_amount).toLocaleString()})</option>
            ))}
          </select>
          <button type="button" onClick={handleAssign} disabled={!selectedBonus || assigning}
            className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-40">
            <Plus size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * En qué estado de vigencia está una asignación (bono o descuento recurrente).
 *
 * ⚠️ Existe porque la ficha muestra el HISTÓRICO —conservarlo es correcto: es el
 * respaldo de lo que se pagó en cada liquidación— y sin esta marca un bono
 * vencido se ve idéntico a uno vigente. Reportado el 2026-08-25: en la ficha de
 * Martin Ramírez aparecían cuatro bonos, dos de ellos terminados en julio, y no
 * había forma de saber cuáles seguían aplicando salvo leer las fechas y
 * compararlas mentalmente con el mes en curso.
 *
 * El cálculo NO usa esto: la vigencia la resuelve el backend
 * (`payroll/views.py:645-647`). Acá es solo para que la pantalla lo diga.
 */
export function estadoDeVigencia(desde?: string | null, hasta?: string | null):
    { vencido: boolean; futuro: boolean; etiqueta: string } {
  const hoy = todayISO()
  if (hasta && hasta < hoy) return { vencido: true, futuro: false, etiqueta: 'Vencido' }
  if (desde && desde > hoy) return { vencido: false, futuro: true, etiqueta: 'Programado' }
  return { vencido: false, futuro: false, etiqueta: '' }
}

function BonusRow({ assignment }: { assignment: any }) {
  const effectiveAmt = assignment.effective_amount || assignment.amount || '0'
  const hasOverride = parseFloat(assignment.amount) > 0
  const [amount, setAmount] = useState(hasOverride ? assignment.amount : '')
  const [from, setFrom] = useState(assignment.effective_from || '')
  const [to, setTo] = useState(assignment.effective_to || '')
  const { mutate: update } = useUpdateEmployeeBonus(assignment.id)
  const { mutate: remove, isPending: removing } = useDeleteEmployeeBonus(assignment.id)
  const { can } = usePermission()
  const canDelete = can('payroll.bonuses', 'delete')
  const vigencia = estadoDeVigencia(from, to)
  const [error, setError] = useState<string | null>(null)

  const conAviso = {
    onSuccess: () => setError(null),
    onError: (e: unknown) => setError(mensajeDeError(e)),
  }

  const handleAmountSave = () => {
    const newAmount = amount || '0'
    if (newAmount !== assignment.amount) {
      update({ amount: newAmount }, conAviso)
    }
  }

  const handleDateSave = (field: 'effective_from' | 'effective_to', value: string) => {
    const next = value || null
    update({ [field]: next }, conAviso)
  }

  return (
    <div>
    <div className={`flex flex-wrap items-center gap-2 px-3 py-2 bg-glass-3 rounded-lg ${vigencia.vencido ? 'opacity-60' : ''}`}>
      <span className="font-mono text-xs text-content-subtle w-12 flex-shrink-0">{assignment.bonus_code}</span>
      <span className="text-sm text-content flex-1 truncate">{assignment.bonus_name}</span>
      {vigencia.etiqueta && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface text-content-subtle border border-divider flex-shrink-0"
              title={vigencia.vencido
                ? 'Terminó: ya no se incluye en las liquidaciones nuevas. Se conserva como respaldo de los períodos en que sí aplicó.'
                : 'Todavía no empieza: no se incluye en la liquidación de este mes.'}>
          {vigencia.etiqueta}
        </span>
      )}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-content-subtle">$</span>
        <input aria-label={`Monto — ${assignment.bonus_name}`} type="number" min="0" step="1" value={amount}
          onChange={e => setAmount(e.target.value)}
          onBlur={handleAmountSave}
          placeholder={parseFloat(effectiveAmt).toLocaleString()}
          title={`Default: $${parseFloat(effectiveAmt).toLocaleString()}. Dejar vacío para usar el default.`}
          className="w-24 px-2 py-0.5 text-xs text-right border border-divider rounded bg-surface focus:ring-1 focus:ring-primary placeholder:text-content-subtle" />
      </div>
      <div className="flex items-center gap-1" title="Vigencia: vacío = sin límite">
        <input aria-label={`Vigente desde — ${assignment.bonus_name}`} type="date" value={from} onChange={e => { setFrom(e.target.value); handleDateSave('effective_from', e.target.value) }}
          className="px-1.5 py-0.5 text-[10px] border border-divider rounded bg-surface" />
        <span className="text-[10px] text-content-subtle">→</span>
        <input aria-label={`Vigente hasta — ${assignment.bonus_name}`} type="date" value={to} onChange={e => { setTo(e.target.value); handleDateSave('effective_to', e.target.value) }}
          className="px-1.5 py-0.5 text-[10px] border border-divider rounded bg-surface" />
      </div>
      <span className="text-[10px] text-content-subtle flex-shrink-0 w-14 text-right">
        {assignment.is_imponible ? 'Imp.' : 'No imp.'}
      </span>
      {canDelete && (
        <button type="button" onClick={() => remove()} disabled={removing}
          title="Quitar bono"
          className="p-1 text-content-subtle hover:text-danger-solid disabled:opacity-40 flex-shrink-0">
          <Trash2 size={13} />
        </button>
      )}
    </div>
    {error && <p className={`${errorCls} px-3`}>{error}</p>}
    </div>
  )
}


// ─── Recurring Deduction Section ────────────────────────────────────────────

function RecurringDeductionSection({ employeeId }: { employeeId: string }) {
  const { data } = useRecurringDeductions({ employee_id: employeeId })
  const { mutate: create } = useCreateRecurringDeduction()
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')

  const items = data?.deductions || []

  const handleAdd = () => {
    if (!name.trim() || !amount) return
    create({ employee: employeeId, name: name.trim(), amount, is_active: true }, {
      onSuccess: () => { setName(''); setAmount('') },
    })
  }

  return (
    <div>
      <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider pt-2 mb-3">
        Descuentos Recurrentes ({items.length})
      </h3>
      <p className="text-xs text-content-subtle mb-2">
        Descuentos voluntarios fijos del trabajador (Seguro Complementario, FALP, créditos). Se aplican cada mes en el rango de vigencia.
      </p>
      {items.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {items.map((d: any) => <RecurringDeductionRow key={d.id} item={d} />)}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input aria-label="Nombre del descuento recurrente" type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder="Nombre (ej. Seguro Compl)"
          className={`${inputCls} flex-1 min-w-0 text-xs`} />
        <input aria-label="Monto del descuento recurrente" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)}
          placeholder="$ Monto"
          className={`${inputCls} !w-32 flex-none text-xs text-right`} />
        <button type="button" onClick={handleAdd} disabled={!name.trim() || !amount}
          className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-40 flex-none">
          <Plus size={12} />
        </button>
      </div>
    </div>
  )
}

function RecurringDeductionRow({ item }: { item: any }) {
  const [amount, setAmount] = useState(item.amount || '')
  const [from, setFrom] = useState(item.effective_from || '')
  const [to, setTo] = useState(item.effective_to || '')
  const { mutate: update } = useUpdateRecurringDeduction(item.id)
  const { mutate: remove, isPending: removing } = useDeleteRecurringDeduction(item.id)
  const { can } = usePermission()
  // El endpoint se gobierna con payroll.payslips (payroll/views.py:1947), no con
  // un módulo propio de descuentos.
  const canDelete = can('payroll.payslips', 'delete')
  const vigencia = estadoDeVigencia(from, to)
  // Un descuento desactivado no se descuenta aunque sus fechas estén vigentes, y
  // antes la fila no lo decía: se veía igual que uno activo (#1771).
  const inactivo = item.is_active === false

  return (
    <div className={`flex flex-wrap items-center gap-2 px-3 py-2 bg-glass-3 rounded-lg ${vigencia.vencido || inactivo ? 'opacity-60' : ''}`}>
      <span className="text-sm text-content flex-1 truncate">{item.name}</span>
      {inactivo && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning-surface text-warning-content border border-warning-border flex-shrink-0"
              title="Desactivado: no se descuenta en las liquidaciones. Poner una fecha «Hasta» de hoy en adelante lo reactiva.">
          Inactivo
        </span>
      )}
      {vigencia.etiqueta && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface text-content-subtle border border-divider flex-shrink-0"
              title={vigencia.vencido
                ? 'Terminó: ya no se descuenta en las liquidaciones nuevas.'
                : 'Todavía no empieza: no se descuenta este mes.'}>
          {vigencia.etiqueta}
        </span>
      )}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-content-subtle">$</span>
        <input aria-label={`Monto — ${item.name}`} type="number" min="0" step="1" value={amount}
          onChange={e => setAmount(e.target.value)}
          onBlur={() => { if (amount !== item.amount) update({ amount }) }}
          className="w-24 px-2 py-0.5 text-xs text-right border border-divider rounded bg-surface focus:ring-1 focus:ring-primary" />
      </div>
      <div className="flex items-center gap-1" title="Vigencia: vacío = sin límite">
        <input aria-label={`Vigente desde — ${item.name}`} type="date" value={from} onChange={e => { setFrom(e.target.value); update({ effective_from: e.target.value || null }) }}
          className="px-1.5 py-0.5 text-[10px] border border-divider rounded bg-surface" />
        <span className="text-[10px] text-content-subtle">→</span>
        <input aria-label={`Vigente hasta — ${item.name}`} type="date" value={to} onChange={e => { setTo(e.target.value); update({ effective_to: e.target.value || null }) }}
          className="px-1.5 py-0.5 text-[10px] border border-divider rounded bg-surface" />
      </div>
      {canDelete && (
        <button type="button" onClick={() => remove()} disabled={removing}
          title="Quitar descuento"
          className="p-1 text-content-subtle hover:text-danger-solid disabled:opacity-40 flex-shrink-0">
          <Trash2 size={13} />
        </button>
      )}
    </div>
  )
}
